import React, { useContext, useRef, useState } from "react";
import styles from "./index.module.scss";
import UserAvatar from "../../../Components/UserAvatar";
import Space from "components/Space";
import { BsThreeDots } from "react-icons/bs";
import { Dropdown, Icon, Menu, message, Modal, Tooltip } from "antd";
import ReplyEditor, { ReplyEditorInstance } from "../ReplyEditor";
import { deleteReply, ReplyItemData, SpeakerType } from "service/discuss";
import { getFriendlyTime } from "utils/common";
import { LikeOrNot } from "../LikeOrNot";
import { errorMessage, isTrue } from "service/service";
import { DiscussContent } from "../../context";
import useStorage from "utils/useStorage";
import moment from "moment";

interface ReplyItemProps {
    data: ReplyItemData;
    parentUuid?: string;
}

export interface EditProps {
    targetUuid: string | null;
    content: string;
}

export default function ReplyItem({ data, parentUuid }: ReplyItemProps) {
    const { updateDiscussDetail } = useContext(DiscussContent);
    const [teacherNo] = useStorage("teacherNo");
    const [content, setContent] = useState<EditProps>({
        targetUuid: null,
        content: ""
    });
    const editorRef = useRef<ReplyEditorInstance>(null);
    const [showEditor, setShowEditor] = useState(false);

    const isTeacher = data.speakerType === SpeakerType.Teacher;
    const isMyPost = data.speakerNo === teacherNo && isTeacher;

    function onReplyFinish() {
        updateDiscussDetail();
        setShowEditor(false);
        setContent({
            targetUuid: null,
            content: ""
        });
    }

    function onDelete() {
        Modal.confirm({
            content: "你确定要删除这条评论吗？",
            onOk: async () => {
                try {
                    await deleteReply(data.topicUuid, data.uuid);
                    message.success("删除成功!");
                    updateDiscussDetail();
                } catch (e) {
                    message.error(errorMessage(e));
                }
            }
        });
    }

    function toggleEditor() {
        setContent({
            targetUuid: null,
            content: ""
        });

        if (!showEditor) return setShowEditor(true);

        if (editorRef.current?.touched()) {
            Modal.confirm({
                content: "要放弃发表评论吗?",
                onOk: () => setShowEditor(false)
            });
        } else {
            setShowEditor(false);
        }
    }

    function onEditShow() {
        setShowEditor(true);
        setContent({
            targetUuid: data.uuid,
            content: data.replyContent
        });
    }

    const menu = (
        <Menu mode="inline">
            <Menu.Item onClick={onEditShow}>修改回复</Menu.Item>
            <Menu.Item onClick={onDelete}>删除评论</Menu.Item>
        </Menu>
    );

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div className={styles.user}>
                    <UserAvatar
                        className={styles.avatar}
                        size={30}
                        isTeacher={isTeacher}
                        userNo={data.speakerNo}
                        userName={data.speakerName}
                    />

                    <div className={styles.userInfo}>
                        <div className={styles.userName}>
                            {data.speakerName}
                            {isTeacher && (
                                <span className={styles.teacher}>老师</span>
                            )}
                        </div>
                        <div className={styles.time}>
                            {getFriendlyTime(data.createTime)}
                            {data.updateTime !== data.createTime && (
                                <Tooltip
                                    title={`重新编辑时间：${moment(
                                        data.updateTime
                                    ).format("YYYY-MM-DD HH:mm:ss")}`}
                                    style={{ marginLeft: 10 }}>
                                    <Icon
                                        type="info-circle"
                                        className={styles.tips}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {data.speakToName && (
                        <div
                            className={styles.speakTo}
                            hidden={parentUuid === data.speakToReplyUuid}>
                            <Icon type="caret-right" /> {data.speakToName}
                        </div>
                    )}
                </div>

                {isTrue(data?.canEdit) && (
                    <Dropdown overlay={menu} placement="bottomCenter">
                        <BsThreeDots />
                    </Dropdown>
                )}
            </div>

            <div className={styles.content}>{data.replyContent}</div>

            <Space className={styles.footer} size={15}>
                <button className={styles.btn} onClick={toggleEditor}>
                    回复
                </button>

                <LikeOrNot answerData={data} />
            </Space>

            {showEditor && (
                <ReplyEditor
                    content={content}
                    setShowEditor={setShowEditor}
                    ref={editorRef}
                    className={styles.editor}
                    speakToReplyUuid={data.uuid}
                    onFinish={onReplyFinish}
                />
            )}

            {data.currentFloorReplies?.length && (
                <div className={styles.subList}>
                    {data.currentFloorReplies?.map(item => (
                        <ReplyItem
                            key={item.uuid}
                            data={item}
                            parentUuid={data.uuid}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
